import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    MessageSquare, 
    Plus, 
    Clock, 
    Bookmark, 
    Search, 
    ArrowLeft, 
    ChevronRight, 
    X, 
    Filter,
    BookOpen 
} from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import Loader from '../components/Loader';
import { toast } from 'react-hot-toast';

const ChatHub = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { isLoaded, isSignedIn } = useAuth();
    const authenticatedFetch = useAuthenticatedFetch();
    
    // Core states
    const [classes, setClasses] = useState([]);
    const [recentChats, setRecentChats] = useState([]);
    const [savedChats, setSavedChats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // UI navigation states
    const [activeTab, setActiveTab] = useState('recent'); // 'recent', 'saved', 'classes'
    const [searchTerm, setSearchTerm] = useState('');
    const [filterIsOpen, setFilterIsOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    
    // Chat creation flow states
    const [chatCreationStep, setChatCreationStep] = useState(0); // 0: inactive, 1: class selection, 2: lecture selection
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedLectures, setSelectedLectures] = useState([]);
    
    useEffect(() => {
        if (!isLoaded || !isSignedIn) return;
        
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Fetch both classes and recent chats in parallel
                const [classesResponse, recentChatsResponse] = await Promise.all([
                    authenticatedFetch('https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes'),
                    authenticatedFetch('https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat/sessions/recent')
                ]);
                
                if (!classesResponse.ok || !recentChatsResponse.ok) {
                    throw new Error('Failed to fetch data');
                }
                
                const classesData = await classesResponse.json();
                const recentChatsData = await recentChatsResponse.json();
                
                setClasses(classesData);
                setRecentChats(recentChatsData.map(chat => ({
                    id: chat.id,
                    title: chat.title || 'Untitled Chat',
                    preview: chat.last_message || 'No messages yet',
                    lastActive: chat.last_message_at || chat.created_at,
                    classInfo: chat.class_info || null,
                    isSaved: chat.is_saved || false
                })));
                
                // Load saved chats (this could be a separate endpoint in a real implementation)
                setSavedChats(recentChatsData.filter(chat => chat.is_saved).map(chat => ({
                    id: chat.id,
                    title: chat.title || 'Untitled Chat',
                    preview: chat.last_message || 'No messages yet',
                    lastActive: chat.last_message_at || chat.created_at,
                    classInfo: chat.class_info || null
                })));
                
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load data');
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchData();
    }, [isLoaded, isSignedIn]);
    
    const loadClassLectures = async (classId) => {
        if (!classId) return;
        
        try {
            const response = await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${classId}`
            );
            
            if (!response.ok) {
                throw new Error('Failed to fetch lectures');
            }
            
            const lectures = await response.json();
            const selectedClassData = classes.find(c => c.id === classId);
            
            setSelectedClass({
                ...selectedClassData,
                lectures: lectures
            });
            
            // Move to lecture selection step
            setChatCreationStep(2);
        } catch (error) {
            console.error('Error fetching lectures:', error);
            toast.error('Failed to load lectures');
        }
    };
    
    const handleNewChatClick = () => {
        setChatCreationStep(1); // Move to class selection
        setSelectedClass(null);
        setSelectedLectures([]);
    };
    
    const handleClassSelect = (classId) => {
        loadClassLectures(classId);
    };
    
    const handleLectureToggle = (lecture) => {
        setSelectedLectures(prev => {
            const isSelected = prev.some(l => l.id === lecture.id);
            return isSelected 
                ? prev.filter(l => l.id !== lecture.id)
                : [...prev, {
                    ...lecture,
                    classId: selectedClass.id,
                    class_id: selectedClass.id
                }];
        });
    };
    
    const startNewChat = () => {
        // If no lectures are selected, use the entire class context
        const context = selectedLectures.length > 0 
            ? { 
                type: 'lectures', 
                items: selectedLectures,
                classInfo: {
                    id: selectedClass.id,
                    title: selectedClass.title,
                    professor: selectedClass.professor
                }
            }
            : { type: 'class', item: selectedClass };
            
        // Navigate to the chat page with context
        navigate('/chat/new', { 
            state: { context },
            replace: true
        });
    };
    
    const handleCancelChatCreation = () => {
        setChatCreationStep(0);
        setSelectedClass(null);
        setSelectedLectures([]);
    };
    
    const handleBackFromLectureSelection = () => {
        setChatCreationStep(1);
        setSelectedLectures([]);
    };
    
    const toggleSaveChat = async (chatId) => {
        // This would be implemented with a real API call
        toast.success('Chat saved successfully');
    };

    const filterChats = (chats) => {
        if (!searchTerm && selectedFilter === 'all') return chats;
        
        return chats.filter(chat => {
            const matchesSearch = !searchTerm || 
                chat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                chat.preview.toLowerCase().includes(searchTerm.toLowerCase());
                
            const matchesFilter = selectedFilter === 'all' || 
                (selectedFilter === 'class' && chat.classInfo);
                
            return matchesSearch && matchesFilter;
        });
    };

    // Render chat creation wizard
    const renderChatCreation = () => {
        if (chatCreationStep === 0) return null;
        
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="relative w-full max-w-2xl rounded-lg shadow-xl p-6" style={{ background: theme.background }}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                            {chatCreationStep === 2 && (
                                <button 
                                    onClick={handleBackFromLectureSelection}
                                    className="mr-4 p-2 rounded-full hover:bg-opacity-80"
                                    style={{ background: theme.secondary }}
                                >
                                    <ArrowLeft size={20} style={{ color: theme.text }} />
                                </button>
                            )}
                            <h2 className="text-xl font-bold" style={{ color: theme.text }}>
                                {chatCreationStep === 1 ? 'Select a Class' : 'Select Lectures (Optional)'}
                            </h2>
                        </div>
                        <button 
                            onClick={handleCancelChatCreation}
                            className="p-2 rounded-full hover:bg-opacity-80"
                            style={{ background: theme.secondary }}
                        >
                            <X size={20} style={{ color: theme.text }} />
                        </button>
                    </div>
                    
                    {/* Content */}
                    {chatCreationStep === 1 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto p-2">
                            {classes.map(cls => (
                                <div
                                    key={cls.id}
                                    onClick={() => handleClassSelect(cls.id)}
                                    className="p-4 rounded-lg cursor-pointer border transition-all duration-200 hover:scale-[1.02] relative overflow-hidden group"
                                    style={{
                                        background: theme.secondary,
                                        borderColor: theme.border,
                                        color: theme.text
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-10"
                                         style={{ from: theme.primary, to: theme.secondary }}></div>
                                    <h3 className="font-semibold mb-2">{cls.title}</h3>
                                    <p className="text-sm" style={{ color: theme.accent }}>
                                        {cls.professor}
                                    </p>
                                    <ChevronRight 
                                        size={18} 
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-60 group-hover:opacity-100"
                                        style={{ color: theme.accent }}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                            <div className="p-4 rounded-lg mb-4" style={{ background: theme.secondary }}>
                                <h3 className="font-bold" style={{ color: theme.text }}>{selectedClass?.title}</h3>
                                <p className="text-sm" style={{ color: theme.accent }}>{selectedClass?.professor}</p>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium" style={{ color: theme.text }}>
                                        {selectedLectures.length === 0 
                                            ? "Select specific lectures or use the entire class" 
                                            : `Selected lectures: ${selectedLectures.length}`}
                                    </h4>
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {selectedClass?.lectures?.map(lecture => (
                                        <div
                                            key={lecture.id}
                                            onClick={() => handleLectureToggle(lecture)}
                                            className={`p-3 rounded-lg cursor-pointer border transition-all flex items-start gap-3 ${
                                                selectedLectures.some(l => l.id === lecture.id)
                                                    ? 'ring-2'
                                                    : ''
                                            }`}
                                            style={{
                                                background: theme.secondary,
                                                borderColor: theme.border,
                                                color: theme.text,
                                                ringColor: theme.primary
                                            }}
                                        >
                                            <div className="mt-1 flex-shrink-0">
                                                <BookOpen size={16} style={{ color: theme.accent }} />
                                            </div>
                                            <div>
                                                <p className="font-medium">{lecture.title}</p>
                                                <p className="text-sm" style={{ color: theme.accent }}>
                                                    {new Date(lecture.date).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Footer */}
                    <div className="mt-6 flex justify-end">
                        {chatCreationStep === 2 && (
                            <button
                                onClick={startNewChat}
                                className="px-6 py-3 rounded-lg flex items-center justify-center gap-2"
                                style={{
                                    background: theme.primary,
                                    color: theme.text
                                }}
                            >
                                <MessageSquare size={18} />
                                Start Chat with {selectedLectures.length > 0 ? `${selectedLectures.length} Lectures` : 'Entire Class'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };
    
    // Render chat card (used for both recent and saved chats)
    const renderChatCard = (chat) => {
        return (
            <div
                key={chat.id}
                className="p-4 rounded-lg cursor-pointer border transition-all duration-200 hover:scale-[1.01] group relative"
                style={{
                    background: theme.secondary,
                    borderColor: theme.border,
                    color: theme.text
                }}
            >
                <div className="flex flex-col h-full">
                    <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold line-clamp-1">{chat.title}</h3>
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleSaveChat(chat.id);
                            }}
                            className="p-1 rounded-full hover:bg-opacity-90 opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: theme.background }}
                        >
                            <Bookmark 
                                size={16} 
                                fill={chat.isSaved ? theme.accent : 'none'} 
                                style={{ color: theme.accent }}
                            />
                        </button>
                    </div>
                    
                    {chat.classInfo && (
                        <div className="text-xs px-2 py-1 rounded-full w-fit mb-2" style={{ background: theme.border, color: theme.accent }}>
                            {chat.classInfo.title}
                        </div>
                    )}
                    
                    <p className="text-sm line-clamp-2 flex-grow" style={{ color: theme.accent }}>
                        {chat.preview}
                    </p>
                    
                    <div className="flex items-center justify-between mt-3 pt-2 border-t" style={{ borderColor: theme.border }}>
                        <span className="text-xs" style={{ color: theme.accent }}>
                            {new Date(chat.lastActive).toLocaleDateString()}
                        </span>
                        
                        <button 
                            onClick={() => navigate(`/chat/${chat.id}`)}
                            className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-opacity-90"
                            style={{ background: theme.background, color: theme.text }}
                        >
                            Open <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };
    
    // Main content renderer
    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-[50vh]">
                    <Loader />
                </div>
            );
        }
        
        let contentToDisplay;
        
        switch (activeTab) {
            case 'classes':
                contentToDisplay = (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {classes.map(cls => (
                            <div
                                key={cls.id}
                                onClick={() => handleClassSelect(cls.id)}
                                className="p-4 rounded-lg cursor-pointer border transition-all duration-200 hover:scale-[1.02] group"
                                style={{
                                    background: theme.secondary,
                                    borderColor: theme.border,
                                    color: theme.text
                                }}
                            >
                                <h3 className="font-semibold mb-2">{cls.title}</h3>
                                <p className="text-sm" style={{ color: theme.accent }}>
                                    {cls.professor}
                                </p>
                                <div className="flex justify-end mt-3">
                                    <button 
                                        className="px-3 py-1 text-sm rounded flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ background: theme.primary, color: theme.text }}
                                    >
                                        <MessageSquare size={14} />
                                        Chat
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                );
                break;
                
            case 'saved':
                const filteredSavedChats = filterChats(savedChats);
                contentToDisplay = filteredSavedChats.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredSavedChats.map(chat => renderChatCard(chat))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <Bookmark size={48} className="mx-auto mb-4 opacity-30" style={{ color: theme.accent }} />
                        <h3 className="text-lg font-medium mb-2" style={{ color: theme.text }}>No saved chats</h3>
                        <p style={{ color: theme.accent }}>Bookmark your important conversations to find them here</p>
                    </div>
                );
                break;
                
            case 'recent':
            default:
                const filteredRecentChats = filterChats(recentChats);
                contentToDisplay = filteredRecentChats.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredRecentChats.map(chat => renderChatCard(chat))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <Clock size={48} className="mx-auto mb-4 opacity-30" style={{ color: theme.accent }} />
                        <h3 className="text-lg font-medium mb-2" style={{ color: theme.text }}>No recent chats</h3>
                        <p style={{ color: theme.accent }}>Start a new conversation to see it here</p>
                    </div>
                );
        }
        
        return contentToDisplay;
    };
    
    return (
        <div className="min-h-screen" style={{ background: theme.background }}>
            {/* Render chat creation wizard */}
            {renderChatCreation()}
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <h1 className="text-2xl font-bold" style={{ color: theme.text }}>
                        Chat with your lectures
                    </h1>
                    
                    <div>
                        <button
                            onClick={handleNewChatClick}
                            className="px-4 py-2 rounded-lg flex items-center gap-2"
                            style={{
                                background: theme.primary,
                                color: theme.text
                            }}
                        >
                            <Plus size={18} />
                            New Chat
                        </button>
                    </div>
                </div>
                
                {/* Tab navigation */}
                <div className="flex items-center border-b mb-6 overflow-x-auto no-scrollbar" style={{ borderColor: theme.border }}>
                    <button
                        onClick={() => setActiveTab('recent')}
                        className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                            activeTab === 'recent' ? 'border-opacity-100' : 'border-transparent'
                        }`}
                        style={{ 
                            color: activeTab === 'recent' ? theme.text : theme.accent,
                            borderColor: theme.primary
                        }}
                    >
                        <Clock size={18} />
                        Recent
                    </button>
                    <button
                        onClick={() => setActiveTab('saved')}
                        className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                            activeTab === 'saved' ? 'border-opacity-100' : 'border-transparent'
                        }`}
                        style={{ 
                            color: activeTab === 'saved' ? theme.text : theme.accent,
                            borderColor: theme.primary
                        }}
                    >
                        <Bookmark size={18} />
                        Saved
                    </button>
                    <button
                        onClick={() => setActiveTab('classes')}
                        className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                            activeTab === 'classes' ? 'border-opacity-100' : 'border-transparent'
                        }`}
                        style={{ 
                            color: activeTab === 'classes' ? theme.text : theme.accent,
                            borderColor: theme.primary
                        }}
                    >
                        <BookOpen size={18} />
                        Classes
                    </button>
                </div>
                
                {/* Search and filters */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-grow">
                        <input
                            type="text"
                            placeholder={`Search ${activeTab}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full p-3 pl-10 rounded-lg border"
                            style={{
                                background: theme.secondary,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        />
                        <Search
                            size={18}
                            className="absolute left-3 top-3.5"
                            style={{ color: theme.accent }}
                        />
                        {searchTerm && (
                            <button 
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-3.5"
                            >
                                <X size={18} style={{ color: theme.accent }} />
                            </button>
                        )}
                    </div>
                    
                    <div className="relative">
                        <button
                            onClick={() => setFilterIsOpen(!filterIsOpen)}
                            className="px-4 py-3 rounded-lg flex items-center gap-2 border"
                            style={{
                                background: theme.secondary,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            <Filter size={18} />
                            {selectedFilter === 'all' ? 'All' : 'Class chats'}
                        </button>
                        
                        {filterIsOpen && (
                            <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl z-10 border" style={{ background: theme.background, borderColor: theme.border }}>
                                <button
                                    onClick={() => {
                                        setSelectedFilter('all');
                                        setFilterIsOpen(false);
                                    }}
                                    className="px-4 py-2 w-full text-left hover:bg-opacity-80 rounded-t-lg"
                                    style={{ 
                                        background: selectedFilter === 'all' ? theme.secondary : 'transparent',
                                        color: theme.text
                                    }}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedFilter('class');
                                        setFilterIsOpen(false);
                                    }}
                                    className="px-4 py-2 w-full text-left hover:bg-opacity-80 rounded-b-lg"
                                    style={{ 
                                        background: selectedFilter === 'class' ? theme.secondary : 'transparent',
                                        color: theme.text
                                    }}
                                >
                                    Class chats
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Main content */}
                {renderContent()}
            </div>
        </div>
    );
};

export default ChatHub; 